import {
  SET_TARGET_CAMPAIGN,
  CAMPAIGN_CREATE_BEGIN,
  CAMPAIGN_CLEAR,
  CAMPAIGN_CREATE_FAILURE,
  CAMPAIGN_CREATE_SUCCESS,
  CAMPAIGN_FETCH_SUCCESS,
  CAMPAIGN_PARTIAL_SAVE_BEGIN,
  CAMPAIGN_PARTIAL_SAVE_SUCCESS,
  CAMPAIGN_PARTIAL_SAVE_FAILURE,
  CAMPAIGN_DELETE_SUCCESS,
  CAMPAIGNS_FETCH_SUCCESS,
  CAMPAIGNS_FETCH_FAILURE,
} from './_types'
import { formatWidgetFromApi } from '@utils/formatWidgetApi'
import campaignAPI from '@api/campaign'
import getErrorMessage from '@utils/getErrorMessage'

const getDefaultState = () => {
  return {
    isCampaignLoaded: false,
    areCampaignsLoaded: false,
    targetCampaignUUID: '',
    currentCampaignsList: [],
    campaignCreationCompleted: false,
    campaignCreationError: false,
    campaignCreationLoading: false,
    campaignActionErrorMessage: '',
    campaignPartialSaveLoading: false,
    campaignPartialSaveError: false,
    campaignPartialSaveCompleted: false,
  }
}
export const state = getDefaultState()

export const getters = {
  /**
   * Returns a target campaign for specific UUID
   */
  targetCampaign: (state) => {
    return state.currentCampaignsList.find(
      campaign => campaign.uuid === state.targetCampaignUUID) || {}
  },
  /**
   * Returns the id of the campaign for a specific UUID
   */
  targetCampaignID: (state, getters) => {
    // Using the targetCampaign getter defined above
    const campaign = getters.targetCampaign
    return campaign.id || null
  },
  /**
   * Returns the watermark presence of the campaign
   */
  targetCampaignWatermark: (state, getters) => {
    // Using the targetCampaign getter defined above
    const campaign = getters.targetCampaign
    return !!campaign.has_watermark
  },
  /**
   * Returns an array with domains across all campaigns
   */
  getAllCampaignDomains: (state) => {
    const domains = state.currentCampaignsList.map(campaign => campaign.domain)
    return [...new Set(domains)]
  },
}

export const mutations = {
  [SET_TARGET_CAMPAIGN](state, uuid) {
    state.targetCampaignUUID = uuid
  },
  [CAMPAIGN_FETCH_SUCCESS](state, campaign) {
    const index = state.currentCampaignsList.findIndex((obj) => {
      return obj.uuid === campaign.uuid
    })

    if (index !== -1) {
      state.currentCampaignsList.splice(index, 1)
    }
    state.currentCampaignsList.push(campaign)
    state.isCampaignLoaded = true
  },
  [CAMPAIGN_PARTIAL_SAVE_BEGIN](state) {
    state.campaignPartialSaveCompleted = false
    state.campaignPartialSaveLoading = true
    state.campaignPartialSaveError = false
    state.campaignActionErrorMessage = ''
  },
  [CAMPAIGN_PARTIAL_SAVE_SUCCESS](state, updatedCampaign) {
    const index = state.currentCampaignsList.findIndex(
      (obj) => obj.uuid === updatedCampaign.uuid)

    if (index !== -1) {
      state.currentCampaignsList.splice(index, 1, updatedCampaign)
    } else {
      state.currentCampaignsList.push(updatedCampaign)
    }

    state.campaignPartialSaveCompleted = true
    state.campaignPartialSaveError = false
    state.campaignPartialSaveLoading = false
  },
  [CAMPAIGN_PARTIAL_SAVE_FAILURE](state, errorMessage) {
    state.campaignPartialSaveError = true
    state.campaignPartialSaveLoading = false
    state.campaignActionErrorMessage = errorMessage
  },
  [CAMPAIGN_DELETE_SUCCESS](state, uuid) {
    for (let i = 0; i < state.currentCampaignsList.length; i++) {
      if (state.currentCampaignsList[i].uuid === uuid) {
        state.currentCampaignsList.splice(i, 1)
      }
    }
  },
  [CAMPAIGN_CREATE_SUCCESS](state) {
    state.campaignCreationCompleted = true
    state.campaignCreationError = false
    state.campaignCreationLoading = false
  },
  [CAMPAIGN_CREATE_BEGIN](state) {
    state.campaignCreationCompleted = false
    state.campaignCreationLoading = true
    state.campaignCreationError = false
    state.campaignActionErrorMessage = ''
  },
  [CAMPAIGN_CLEAR](state) {
    Object.assign(state, getDefaultState())
  },
  [CAMPAIGN_CREATE_FAILURE](state, errorMessage) {
    state.campaignCreationError = true
    state.campaignActionErrorMessage = errorMessage
    state.campaignCreationLoading = false
  },
  [CAMPAIGNS_FETCH_SUCCESS](state, data) {
    state.currentCampaignsList = data
    state.areCampaignsLoaded = true
  },
  [CAMPAIGNS_FETCH_FAILURE](state) {
    state.areCampaignsLoaded = false
  },
}

export const actions = {
  /**
   * Returns a JSON with the campaign matching the ID stated
   */
  getCampaign({ commit, dispatch }, id) {
    return campaignAPI.getCampaign(id).then((response) => {
      const targetCampaign = { ...response.data }
      const formattedWidgetItems = targetCampaign.widgetItems.map(widget =>
        formatWidgetFromApi(widget),
      )

      dispatch('widget/setWidgetItems', formattedWidgetItems)
      dispatch('setTargetCampaign', targetCampaign.uuid)
      commit(CAMPAIGN_FETCH_SUCCESS, targetCampaign)
    }).catch(() => {
      return false
    })
  },
  /**
   * Returns a JSON with all campaigns for this user
   */
  getAllCampaigns({ commit }) {
    return campaignAPI.getAllCampaigns().then((response) => {
      const sortedCampaignsByDate = response.data.sort((a, b) =>
        a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
      )
      commit(CAMPAIGNS_FETCH_SUCCESS, sortedCampaignsByDate)
      return response
    }).catch(() => commit(CAMPAIGNS_FETCH_FAILURE))
  },
  createCampaign({ commit, rootState }, { name, domainID, widgetItems }) {
    const data = {
      name,
      domainID,
      userID: rootState.users.currentUser.id,
      widgetItems,
    }

    commit(CAMPAIGN_CREATE_BEGIN)

    return campaignAPI.createCampaign(data)
      .then((response) => {
        const uuid = response?.data?.uuid
        if (!uuid) {
          throw new Error('UUID is missing in the response.')
        }

        if (window.Tawk_API && typeof window.Tawk_API.addEvent === 'function') {
          try {
            window.Tawk_API.addEvent('create-campaign', { 'uuid': uuid },
              function (error) {
                if (error) {
                  console.error('Error adding event to Tawk_API:', error)
                }
              })
          } catch (error) {
            console.error('Error Tawk_API')
          }
        } else {
          console.warn('Tawk_API or addEvent not available.')
        }

        commit(CAMPAIGN_CREATE_SUCCESS)
        return response
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)
        commit(CAMPAIGN_CREATE_FAILURE, errorMessage)

        return Promise.resolve()
      })
  },
  partialSaveCampaign({ commit, state, getters }, campaign) {
    if (!campaign.uuid) {
      campaign.uuid = state.targetCampaignUUID
    }

    commit(CAMPAIGN_PARTIAL_SAVE_BEGIN)

    return campaignAPI.partialSaveCampaign(campaign).then((response) => {
      commit(CAMPAIGN_PARTIAL_SAVE_SUCCESS, response.data)
    }).catch((error) => {
      const errorMessage = getErrorMessage(error)
      commit(CAMPAIGN_PARTIAL_SAVE_FAILURE, errorMessage)
      return error
    })
  },
  setTargetCampaign({ commit }, uuid) {
    commit(SET_TARGET_CAMPAIGN, uuid)
  },
  deleteCampaign({ commit }, uuid) {
    return campaignAPI.deleteCampaign(uuid).then(() => {
      commit(CAMPAIGN_DELETE_SUCCESS, uuid)
    })
  },
  clearState({ commit }) {
    commit(CAMPAIGN_CLEAR)
  },
}
